import { Flex, Spinner, useToast } from "@chakra-ui/react";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router";

import { registrationTypeOptionsType } from "@nens/brostar/uploadapi-client";
import { useOnlyOnce } from "@nens/react/hooks";

import { convertFRDToEditTask } from "@/helpers/convert-frd-to-task";
import {
  convertGARToEditTask,
  convertGARUploadTaskToCreateTask,
} from "@/helpers/convert-gar-to-task";
import {
  convertGLDAdditionToEditTask,
  convertGLDToEditTask,
  convertGLDUploadTaskToCreateTask,
} from "@/helpers/convert-gld-to-task";
import { convertGMNMeasuringPointToEditTask } from "@/helpers/convert-gmn-measuring-point-to-task";
import {
  convertGMNToEditTask,
  convertGMNUploadTaskToCreateTask,
} from "@/helpers/convert-gmn-to-task";
import {
  convertGMWEventToEditTask,
  convertGMWEventUploadTaskToCreateTask,
} from "@/helpers/convert-gmw-event-to-task";
import {
  convertGMWToEditTask,
  convertGMWUploadTaskToCreateTask,
} from "@/helpers/convert-gmw-to-task";
import { convertGPDReportToEditTask, convertGPDToEditTask } from "@/helpers/convert-gpd-to-task";
import { convertGUFEventToEditTask } from "@/helpers/convert-guf-event-to-task";
import { convertGUFToEditTask } from "@/helpers/convert-guf-to-task";
import { useFrdSingleQueryDirectly } from "@/queries/query-frd";
import { useGarSingleQueryDirectly } from "@/queries/query-gar";
import { useGarLaboratoryAnalysisQueryDirectly } from "@/queries/query-gar-analysis";
import { useGarFieldMeasurementsAllPagesQueryDirectly } from "@/queries/query-gar-field-measurements";
import { useGldSingleQueryDirectly } from "@/queries/query-gld";
import { useGldObservationSingleQueryDirectly } from "@/queries/query-gld-observation";
import { useGmnSingleQueryDirectly } from "@/queries/query-gmn";
import {
  useGmnMeasuringPointListAllPagesQueryDirectly,
  useGmnMeasuringPointSingleQueryDirectly,
} from "@/queries/query-gmn-measuring-point";
import { useGmwSingleQueryDirectly } from "@/queries/query-gmw";
import { useGmwEventSingleQueryDirectly } from "@/queries/query-gmw-event";
import { useGmwMonitoringtubesAllPagesQueryDirectly } from "@/queries/query-gmw-monitoringtubes";
import {
  useGpdReportSingleQueryDirectly,
  useGpdSingleQueryDirectly,
  useGpdVolumeSeriesListQueryDirectly,
} from "@/queries/query-gpd";
import {
  getInstallations,
  useGufEnergycharacteristicsAllPagesQueryDirectly,
  useGufEventSingleQueryDirectly,
  useGufLoopsAllPagesQueryDirectly,
  useGufSingleQueryDirectly,
  useGufSurfaceinfiltrationsAllPagesQueryDirectly,
  useGufWellsAllPagesQueryDirectly,
} from "@/queries/query-guf";
import { useUploadTaskSingleQueryDirectly } from "@/queries/query-upload-task";

type FormType =
  | "uploadTask"
  | "gmn"
  | "gmnMeasuringPoint"
  | "gmw"
  | "gmwEvent"
  | "gar"
  | "gld"
  | "gldAddition"
  | "gpd"
  | "gpdReport"
  | "gpdEnd"
  | "frd"
  | "frdMeasurementConfig"
  | "frdMeasurement"
  | "guf"
  | "gufEvent";

// TODO: MAKE IT WORK CORRECTLY WITH GAR
type SendObjectProps<T> = {
  type: FormType;
  uuid?: string;
  renderFunction: (formProps: T) => ReactNode;
};

export function isFormType(type?: string): type is FormType {
  return (
    type !== undefined &&
    [
      "uploadTask",
      "gmn",
      "gmnMeasuringPoint",
      "gmw",
      "gmwEvent",
      "gar",
      "gld",
      "gldAddition",
      "gpd",
      "gpdReport",
      "gpdEnd",
      "frd",
      "frdMeasurementConfig",
      "frdMeasurement",
      "guf",
      "gufEvent",
    ].includes(type)
  );
}

export function SendObjectToForm<T>({ type, uuid, renderFunction }: SendObjectProps<T>) {
  const [formProps, setFormProps] = useState<T | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const fetchUploadTask = useUploadTaskSingleQueryDirectly();
  const fetchGmw = useGmwSingleQueryDirectly();
  const fetchGmwEvent = useGmwEventSingleQueryDirectly();
  const fetchGld = useGldSingleQueryDirectly();
  const fetchGldObservation = useGldObservationSingleQueryDirectly();
  const fetchGpd = useGpdSingleQueryDirectly();
  const fetchGpdReport = useGpdReportSingleQueryDirectly();
  const fetchGmnMeasuringPoint = useGmnMeasuringPointSingleQueryDirectly();
  const fetchGmn = useGmnSingleQueryDirectly();
  const fetchMeasuringPoints = useGmnMeasuringPointListAllPagesQueryDirectly();
  const fetchMonitoringTubes = useGmwMonitoringtubesAllPagesQueryDirectly();
  const fetchGar = useGarSingleQueryDirectly();
  const fetchGarFieldMeasurements = useGarFieldMeasurementsAllPagesQueryDirectly();
  const fetchGarLaboratoryAnalysis = useGarLaboratoryAnalysisQueryDirectly();
  const fetchFrd = useFrdSingleQueryDirectly();
  const fetchGuf = useGufSingleQueryDirectly();
  const fetchGufEvent = useGufEventSingleQueryDirectly();
  const fetchGufWells = useGufWellsAllPagesQueryDirectly();
  const fetchGufLoops = useGufLoopsAllPagesQueryDirectly();
  const fetchGufSurfaceInfiltrations = useGufSurfaceinfiltrationsAllPagesQueryDirectly();
  const fetchGufEnergyCharacteristics = useGufEnergycharacteristicsAllPagesQueryDirectly();
  const fetchVolumeSeries = useGpdVolumeSeriesListQueryDirectly();

  useOnlyOnce(async () => {
    if (!uuid) return navigate("/");
    try {
      if (type === "uploadTask") {
        const result = await fetchUploadTask(uuid);

        const task = (() => {
          if (result.broDomain === "GAR") return convertGARUploadTaskToCreateTask(result);
          if (result.broDomain === "GMN") return convertGMNUploadTaskToCreateTask(result);
          if (result.broDomain === "GMW") {
            // GMW Event
            if ((registrationTypeOptionsType as string[]).includes(result.registrationType))
              return convertGMWEventUploadTaskToCreateTask(result);

            // GMW
            return convertGMWUploadTaskToCreateTask(result);
          }
          if (result.broDomain === "GLD") return convertGLDUploadTaskToCreateTask(result);
        })();

        setFormProps({
          task,
          type: "upload",
          requestType: result.requestType,
          oldUploadTask: result,
        } as T);
      } else if (type === "gmw") {
        const result = await fetchGmw(uuid);
        const monitoringTubesResult = await fetchMonitoringTubes({ gmwBroId: result.broId });
        setFormProps({
          task: convertGMWToEditTask(result, monitoringTubesResult.data?.results ?? []),
          uuid: result.uuid,
          broId: result.broId,
          type: "edit",
        } as T);
        // GMW-Event
      } else if (type === "gmwEvent") {
        const result = await fetchGmwEvent(uuid);
        const gmw = await fetchGmw(result.gmw);
        setFormProps({
          task: convertGMWEventToEditTask(result, gmw),
          uuid: result.uuid,
          broId: result.gmwBroId,
          type: "edit",
        } as T);
      } else if (type === "gld") {
        const result = await fetchGld(uuid);
        setFormProps({
          task: convertGLDToEditTask(result),
          uuid: result.uuid,
          broId: result.broId,
          type: "edit",
        } as T);
      } else if (type === "gldAddition") {
        const result = await fetchGldObservation(uuid);
        const gld = await fetchGld(result.gld);
        setFormProps({
          task: convertGLDAdditionToEditTask(result, gld),
          uuid: result.uuid,
          broId: gld.broId,
          type: "edit",
        } as T);
      } else if (type === "gpd") {
        const result = await fetchGpd(uuid);
        setFormProps({
          task: convertGPDToEditTask(result),
          uuid: result.uuid,
          broId: result.broId,
          type: "edit",
        } as T);
      } else if (type === "gpdReport") {
        const report = await fetchGpdReport(uuid);
        const gpd = await fetchGpd(report.gpd);
        const volumeSeries = (await fetchVolumeSeries({ report: report.uuid })).data?.results ?? [];
        setFormProps({
          task: convertGPDReportToEditTask(report, gpd, volumeSeries),
          uuid: report.uuid,
          broId: gpd.broId,
          type: "edit",
        } as T);
      } else if (type === "gpdEnd") {
        const result = await fetchGpd(uuid);
        setFormProps({
          task: {
            metadata: {
              projectNumber: "",
              broId: result.broId,
              qualityRegime: result.qualityRegime ?? "IMBRO",
              deliveryAccountableParty: result.deliveryAccountableParty ?? "",
            },
          },
          uuid: result.uuid,
          broId: result.broId,
          type: "edit",
        } as T);
      } else if (type === "gmnMeasuringPoint") {
        const result = await fetchGmnMeasuringPoint(uuid);
        const gmn = await fetchGmn(result.gmn);
        setFormProps({
          task: convertGMNMeasuringPointToEditTask(gmn, result),
          uuid: result.uuid,
          broId: gmn.broId,
          type: "edit",
        } as T);
      } else if (type === "gar") {
        // Retrieve the fieldmeasurements and laboratory analyses in parallel from their respective
        // API endpoints. The uuid of the GAR is used to query.
        const [gar, fieldMeasurements, labData] = await Promise.all([
          fetchGar(uuid),
          fetchGarFieldMeasurements(uuid ?? ""),
          fetchGarLaboratoryAnalysis(uuid ?? ""),
        ]);

        setFormProps({
          task: convertGARToEditTask(gar, fieldMeasurements, labData),
          uuid: gar.uuid,
          broId: gar.broId,
          type: "edit",
        } as T);
      } else if (type === "frd") {
        const result = await fetchFrd(uuid);
        const hasLinkedGmns = Array.isArray(result.linkedGmns) && result.linkedGmns.length > 0;
        if (!hasLinkedGmns) {
          toast({
            title: "Geen gekoppelde meetnetten",
            description:
              "Er zijn geen GMN's gevonden die gekoppeld zijn aan dit FRD object. De velden kunnen handmatig worden ingevuld.",
            status: "warning",
            duration: 7000,
            isClosable: true,
          });
        }
        setFormProps({
          task: convertFRDToEditTask(result),
          uuid: result.uuid,
          broId: result.broId,
          type: "edit",
        } as T);
      } else if (type === "guf") {
        const result = (await fetchGuf(uuid)).data;
        if (!result) {
          throw new Error("GUF not found");
        }
        const designInstallations = await getInstallations(result.installations ?? [], {
          fetchGufWells,
          fetchGufLoops,
          fetchGufSurfaceInfiltrations,
          fetchGufEnergyCharacteristics,
        });
        setFormProps({
          task: convertGUFToEditTask(result, designInstallations),
          uuid: result.uuid,
          broId: result.broId,
          type: "edit",
        } as T);
      } else if (type === "gufEvent") {
        const result = (await fetchGufEvent(uuid)).data;
        if (!result) throw new Error("GUF event not found");
        setFormProps({
          task: convertGUFEventToEditTask(result),
          uuid: result.uuid,
          broId: (result.metadata as Record<string, string>)?.broId ?? "",
          type: "edit",
        } as T);
      } else {
        // GMN
        const gmn = await fetchGmn(uuid);

        const [measuringPointsResult, monitoringTubesResult] = await Promise.all([
          fetchMeasuringPoints({
            gmn: uuid,
            eventType: "GMN_StartRegistration",
          }),
          fetchMonitoringTubes({ gmnBroId: gmn.broId }),
        ]);

        setFormProps({
          task: convertGMNToEditTask(
            gmn,
            measuringPointsResult.data?.results ?? [],
            monitoringTubesResult.data?.results ?? []
          ),
          uuid: gmn.uuid,
          broId: gmn.broId,
          type: "edit",
        } as T);
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "An error occurred while fetching the data.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      navigate("/");
    }
  });

  if (!formProps)
    return (
      <Flex boxSize="full" justify="center" align="center">
        <Spinner size="xl" />
      </Flex>
    );

  return renderFunction(formProps as T);
}
